import { useEffect, useState, type ReactNode } from 'react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { AppLoader } from '../components/AppLoader';
import { useAuth } from './AuthContext';

/** Top-level gate: for a signed-in visitor, hold the ENTIRE app on a full-screen
 *  splash until `me` resolves, so a hard refresh never flashes a route (or the
 *  home dashboard) before we know who the user is. Anonymous visitors (no token)
 *  pass straight through to the public routes (landing / login).
 *
 *  This is the single choke point for the "home page flashes for a second on
 *  refresh" bug: nothing downstream renders until auth is known, so no page
 *  component, app shell, or redirect can paint first. `me` here shares its query
 *  key with ProtectedRoute and the pages, so they all read it from cache with no
 *  extra request. Gated on `isLoading` (not `!me`) so an errored/expired token
 *  doesn't wedge the splash — it falls through and ProtectedRoute handles it. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const crud = useClientQueries(schema);
  const meQ = crud.$procs.me.useQuery(undefined, { enabled: !!token, retry: false });

  // Safety net: never let the splash wedge forever if `me` never settles (e.g. a
  // cold-started API that doesn't answer). After a few seconds, fall through — the
  // routes + the authFetch 401 interceptor then take over (redirect to /login).
  const waiting = !!token && meQ.isLoading;
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!waiting) {
      setTimedOut(false);
      return;
    }
    const id = window.setTimeout(() => setTimedOut(true), 8000);
    return () => window.clearTimeout(id);
  }, [waiting]);

  if (waiting && !timedOut) {
    return <AppLoader fullscreen />;
  }
  return <>{children}</>;
}
